/**
 * EibExcludeRiskyToggle — the per-flight "exclude risky" option for an Event in a Box invitation.
 *
 * Ordinary flights set this in the flight form. Event in a Box flights cannot be edited (the database locks
 * them), so this calls the one sanctioned RPC, eib_set_exclude_risky (migration 2026-10-08-394), which changes
 * only this option, only on this event's invitation emails that have not started sending. Off by default:
 * risky addresses are sent. Same wording as the flight form.
 */
import { useState } from "react";
import { setEibExcludeRisky } from "../../services/campaigns.service";

interface Props {
  flightId: string;
  flightStatus: string;
  excludeRisky: boolean;
  /** Reload the flight after a change. */
  onChanged: () => void;
}

export default function EibExcludeRiskyToggle({
  flightId,
  flightStatus,
  excludeRisky,
  onChanged,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editable = flightStatus === "draft" || flightStatus === "scheduled";

  const change = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await setEibExcludeRisky(flightId, next);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change this option");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 pb-4" data-testid="eib-exclude-risky">
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="eib_exclude_risky"
            checked={excludeRisky}
            disabled={!editable || busy}
            onChange={(e) => void change(e.target.checked)}
            className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
          />
          <div className="flex-1">
            <label
              htmlFor="eib_exclude_risky"
              className={`text-sm font-medium ${editable ? "text-gray-900" : "text-gray-500"}`}
            >
              Exclude risky (unverified) addresses from this send
            </label>
            <p className="text-xs text-gray-500 mt-0.5">
              Risky = NeverBounce could not verify the address (it may or may
              not be deliverable). Off by default, so these are included.
              Undeliverable (invalid) addresses are always excluded, whether or
              not this is on. For this event, the choice applies to its
              invitation and reminder emails that have not started sending.
            </p>
            {!editable && (
              <p className="text-xs text-gray-500 mt-1">
                This email has already started sending, so this can no longer be
                changed.
              </p>
            )}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
